// Character state: X lerp toward pointer, emotion timer, body-frame anim.
// Doc: docs/gameplay-config.md#character.

import {
  BODY_ANIM_FPS,
  BODY_ANIM_FREEZE_FRAME,
  CHARACTER_FOLLOW_LERP,
  HEAD_EMOTION_DURATION_S,
  HEAD_EMOTION_INTERRUPT,
} from "../config/gameplay";
import { SCENE_WIDTH } from "../config/scene";
import type { Emotion } from "../state/types";

export type CharacterState = {
  x: number;
  targetX: number;
  facing: "left" | "right";
  emotion: Emotion;
  emotionTimer: number; // seconds remaining; 0 = neutral
  frameAccum: number; // seconds since last frame advance
  frameIndex: 0 | 1 | 2 | 3;
};

export function createCharacter(): CharacterState {
  return {
    x: SCENE_WIDTH / 2,
    targetX: SCENE_WIDTH / 2,
    facing: "right",
    emotion: "neutral",
    emotionTimer: 0,
    frameAccum: 0,
    frameIndex: BODY_ANIM_FREEZE_FRAME as 0,
  };
}

export function updateCharacter(state: CharacterState, dt: number): void {
  // Movement lerp.
  const prevX = state.x;
  state.x = state.x + (state.targetX - state.x) * CHARACTER_FOLLOW_LERP;

  // Facing direction: flip only on movement, sticky otherwise.
  const delta = state.x - prevX;
  if (delta > 0.5) state.facing = "right";
  else if (delta < -0.5) state.facing = "left";

  // Body animation: cycle 0..3 at BODY_ANIM_FPS while moving; freeze otherwise.
  const moving = Math.abs(delta) > 0.5;
  if (moving) {
    state.frameAccum += dt;
    const stride = 1 / BODY_ANIM_FPS;
    while (state.frameAccum >= stride) {
      state.frameAccum -= stride;
      state.frameIndex = ((state.frameIndex + 1) & 3) as 0 | 1 | 2 | 3;
    }
  } else {
    state.frameAccum = 0;
    state.frameIndex = BODY_ANIM_FREEZE_FRAME as 0;
  }

  // Emotion timer.
  if (state.emotionTimer > 0) {
    state.emotionTimer -= dt;
    if (state.emotionTimer <= 0) {
      state.emotionTimer = 0;
      state.emotion = "neutral";
    }
  }
}

export function setCharacterEmotion(state: CharacterState, e: Emotion): void {
  if (e === "neutral") return;
  if (state.emotionTimer > 0 && !HEAD_EMOTION_INTERRUPT) return;
  state.emotion = e;
  state.emotionTimer = HEAD_EMOTION_DURATION_S;
}
